import { useEffect, useMemo, useRef, useState } from "react";
import {
  Info,
  RotateCcw,
  ScanLine,
  TestTube2,
  UploadCloud,
  Egg,
  Ruler,
  FlaskConical,
  type LucideIcon,
} from "lucide-react-native";
import { ScrollView, useWindowDimensions, View } from "react-native";
import {
  CompactActionButton,
  Dialog,
  DialogActions,
  DialogHeader,
  DialogIcon,
  PrimaryButton,
  SecondaryButton,
  IconButton,
  DateField,
  MoonDetailRow,
  MoonFormField,
  MoonValueInput,
  colors,
  semanticColors,
  ActionBanner,
  MoonSegmentedControl,
  CardSurface,
  type MoonSegmentedOption,
  MetricValueText,
  TextField,
} from "@louise/moon-ui";
import { MoonText as Text } from "@louise/moon-ui/primitives/MoonText";
import { useTranslations } from "@src/packages/i18n";
import { LabValueField } from "@src/packages/test-results/components/LabValueField";
import type { DocumentUploadSaveResult } from "@src/packages/document-vault/DocumentUploadModal";

const MONITORING_DIALOG_ILLUSTRATION =
  require("@src/apps/moon/assets/images/monitoring-dialog-illustration.png") as number;

export const MONITORING_UNIT_DEFAULTS = {
  estradiol: "pg/mL",
  lh: "mIU/mL",
  fsh: "mIU/mL",
  progesterone: "ng/mL",
} as const;

export type MonitoringEntryDraft = {
  leftSizes: string;
  rightSizes: string;
  leftCount: string;
  rightCount: string;
  liningThickness: string;
  estradiol: string;
  estradiolUnit: string;
  lh: string;
  lhUnit: string;
  fsh: string;
  fshUnit: string;
  progesterone: string;
  progesteroneUnit: string;
  notes: string;
};

export type MonitoringPreviousResult = {
  date: string;
  leftSizes: string;
  rightSizes: string;
  leftCount: string;
  rightCount: string;
  liningThickness: string;
  estradiol: string;
  estradiolUnit: string;
  lh: string;
  lhUnit: string;
  progesterone: string;
  progesteroneUnit: string;
};

type DraftKey = keyof MonitoringEntryDraft;
type ValidationErrors = Partial<Record<DraftKey | "date" | "form", string>>;
type MonitoringSection = "ultrasound" | "bloodwork";

type MonitoringEntryModalProps = {
  open: boolean;
  dateISO: string;
  current: MonitoringEntryDraft;
  previous: MonitoringPreviousResult | null;
  onClose: () => void;
  onDateChange?: (dateISO: string) => void;
  onSave: (
    draft: MonitoringEntryDraft,
    sourceDocumentIds: string[],
  ) => Promise<void>;
  onUploadReport?: MonitoringUploadRequest;
};

export type MonitoringUploadRequest = (
  dateISO: string,
  onMonitoringExtracted: (data: Record<string, unknown>) => void,
  onDocumentSaved?: (result: DocumentUploadSaveResult) => void,
) => void;

const MONITORING_VALUE_KEYS: DraftKey[] = [
  "leftSizes",
  "rightSizes",
  "leftCount",
  "rightCount",
  "liningThickness",
  "estradiol",
  "lh",
  "fsh",
  "progesterone",
  "notes",
];

const parseNumberList = (raw: string): number[] =>
  raw
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean)
    .map(Number)
    .filter((value) => Number.isFinite(value));

function validateNumber(
  value: string,
  max: number,
  label: string,
): string | null {
  if (!value.trim()) return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > max) {
    return `${label} must be between 0 and ${max}.`;
  }
  return null;
}

function validateSizeList(value: string, label: string): string | null {
  if (!value.trim()) return null;
  const tokens = value.split(",").map((token) => token.trim());
  if (tokens.some((token) => !token || !/^\d+(\.\d+)?$/.test(token))) {
    return `${label} must use comma-separated numbers.`;
  }
  if (parseNumberList(value).some((size) => size < 0 || size > 50)) {
    return `${label} must be between 0 and 50 mm.`;
  }
  return null;
}

function formatDateLabel(dateISO: string): string {
  const date = new Date(`${dateISO}T12:00:00`);
  if (Number.isNaN(date.getTime())) return dateISO;
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

function formatISODate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

function stringValue(value: unknown): string {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number") return String(value);
  if (Array.isArray(value)) {
    return value
      .map((item) => stringValue(item))
      .filter(Boolean)
      .join(", ");
  }
  return "";
}

function monitoringValuesFromOCR(
  data: Record<string, unknown>,
): Partial<MonitoringEntryDraft> {
  const valueFor = (...keys: string[]) => {
    for (const key of keys) {
      const value = stringValue(data[key]);
      if (value) return value;
    }
    return "";
  };
  const results = Array.isArray(data.results)
    ? data.results.filter(
        (result): result is Record<string, unknown> =>
          !!result && typeof result === "object",
      )
    : [];
  const resultFor = (pattern: RegExp) => {
    const result = results.find((item) =>
      pattern.test(stringValue(item.marker)),
    );
    return result
      ? {
          value: stringValue(result.value),
          unit: stringValue(result.unit),
        }
      : { value: "", unit: "" };
  };

  const estradiol = resultFor(/estradiol|^e2$/i);
  const lh = resultFor(/^lh$/i);
  const fsh = resultFor(/^fsh$/i);
  const progesterone = resultFor(/progesterone|^p4$/i);
  const notes = valueFor("monitoringNotes", "findings");
  const next: Partial<MonitoringEntryDraft> = {};

  const fields: [keyof MonitoringEntryDraft, string][] = [
    [
      "leftSizes",
      valueFor("leftFollicleSizes", "follicleSizesLeft", "leftFollicles"),
    ],
    [
      "rightSizes",
      valueFor("rightFollicleSizes", "follicleSizesRight", "rightFollicles"),
    ],
    ["leftCount", valueFor("leftFollicleCount", "follicleCountLeft")],
    ["rightCount", valueFor("rightFollicleCount", "follicleCountRight")],
    [
      "liningThickness",
      valueFor(
        "endometrialThickness",
        "endometrialLiningThickness",
        "liningThickness",
      ),
    ],
    ["estradiol", valueFor("estradiol") || estradiol.value],
    ["estradiolUnit", valueFor("estradiolUnit") || estradiol.unit],
    ["lh", valueFor("lh") || lh.value],
    ["lhUnit", valueFor("lhUnit") || lh.unit],
    ["fsh", valueFor("fsh") || fsh.value],
    ["fshUnit", valueFor("fshUnit") || fsh.unit],
    ["progesterone", valueFor("progesterone") || progesterone.value],
    ["progesteroneUnit", valueFor("progesteroneUnit") || progesterone.unit],
    ["notes", notes],
  ];

  fields.forEach(([key, value]) => {
    if (value) next[key] = value;
  });
  return next;
}

function FieldInput({
  value,
  onChangeText,
  placeholder,
  accessibilityLabel,
  hasError = false,
  multiline = false,
  editable = true,
  keyboardType = "default",
  suffix,
}: {
  value: string;
  onChangeText: (value: string) => void;
  placeholder?: string;
  accessibilityLabel: string;
  hasError?: boolean;
  multiline?: boolean;
  editable?: boolean;
  keyboardType?: "default" | "number-pad" | "decimal-pad";
  suffix?: string;
}) {
  if (!multiline) {
    // A one-line value is the shared value box, its unit inside it.
    return (
      <MoonValueInput
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        accessibilityLabel={accessibilityLabel}
        hasError={hasError}
        disabled={!editable}
        keyboardType={keyboardType}
        suffix={suffix}
      />
    );
  }
  return (
    <TextField
      notes
      value={value}
      onChangeText={onChangeText}
      placeholder={placeholder}
      accessibilityLabel={accessibilityLabel}
      hasError={hasError}
      editable={editable}
      keyboardType={keyboardType}
    />
  );
}

function FormInput({
  label,
  hint,
  error,
  value,
  onChangeText,
  placeholder,
  accessibilityLabel,
  multiline,
  editable = true,
  keyboardType,
  suffix,
}: {
  label: string;
  hint?: string;
  error?: string;
  value: string;
  onChangeText: (value: string) => void;
  placeholder?: string;
  accessibilityLabel: string;
  multiline?: boolean;
  editable?: boolean;
  keyboardType?: "default" | "number-pad" | "decimal-pad";
  /** The value's unit, written inside the field. */
  suffix?: string;
}) {
  return (
    <MoonFormField
      label={label}
      labelHint={hint}
      error={error}
      disabled={!editable}
    >
      <FieldInput
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        accessibilityLabel={accessibilityLabel}
        hasError={!!error}
        multiline={multiline}
        editable={editable}
        keyboardType={keyboardType}
        suffix={suffix}
      />
    </MoonFormField>
  );
}

/** One derived or previous value, the label over it. */
function SummaryDetail({
  icon,
  label,
  value,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
}) {
  return (
    <MoonDetailRow
      icon={icon}
      label={label}
      value={<MetricValueText size="compact">{value || "—"}</MetricValueText>}
      style={{ flex: 1, minWidth: 92 }}
    />
  );
}

function MonitoringEntryTabs({
  active,
  onChange,
}: {
  active: MonitoringSection;
  onChange: (section: MonitoringSection) => void;
}) {
  const options: MoonSegmentedOption<MonitoringSection>[] = [
    {
      value: "ultrasound",
      label: "Ultrasound",
      icon: ScanLine,
    },
    {
      value: "bloodwork",
      label: "Bloodwork",
      icon: TestTube2,
    },
  ];
  return (
    <MoonSegmentedControl tabs={options} active={active} onChange={onChange} />
  );
}

export function MonitoringEntryModal({
  open,
  dateISO,
  current,
  previous,
  onClose,
  onDateChange,
  onSave,
  onUploadReport,
}: MonitoringEntryModalProps) {
  const t = useTranslations();
  const { height } = useWindowDimensions();
  const todayISO = useMemo(() => formatISODate(new Date()), []);
  const [draft, setDraft] = useState<MonitoringEntryDraft>(current);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [section, setSection] = useState<MonitoringSection>("ultrasound");
  const [sourceNotice, setSourceNotice] = useState(false);
  const [sourceDocumentIds, setSourceDocumentIds] = useState<string[]>([]);
  const formScrollRef = useRef<ScrollView>(null);
  const previousOpenRef = useRef(open);
  const previousDateISORef = useRef(dateISO);

  useEffect(() => {
    const justOpened = open && !previousOpenRef.current;
    const dateChanged = dateISO !== previousDateISORef.current;
    previousOpenRef.current = open;
    previousDateISORef.current = dateISO;

    // A mutation invalidates the source query while this dialog is open. The
    // resulting `current` object may change, but it must not replace the
    // user's in-progress draft after a partial save. Reset only for a new
    // dialog session or an intentional date change.
    if (!open || (!justOpened && !dateChanged)) return;
    setDraft(current);
    setErrors({});
    setSaveFailed(false);
    setSection("ultrasound");
    setSourceNotice(false);
    setSourceDocumentIds([]);
  }, [current, dateISO, open]);

  const leftSizes = useMemo(
    () => parseNumberList(draft.leftSizes),
    [draft.leftSizes],
  );
  const rightSizes = useMemo(
    () => parseNumberList(draft.rightSizes),
    [draft.rightSizes],
  );
  const leadSize = Math.max(...leftSizes, ...rightSizes, 0);
  const leftCount =
    leftSizes.length > 0 ? String(leftSizes.length) : draft.leftCount;
  const rightCount =
    rightSizes.length > 0 ? String(rightSizes.length) : draft.rightCount;
  const previousLeftCount = previous
    ? parseNumberList(previous.leftSizes).length || previous.leftCount
    : "";
  const previousRightCount = previous
    ? parseNumberList(previous.rightSizes).length || previous.rightCount
    : "";
  const previousLeadSize = previous
    ? Math.max(
        ...parseNumberList(previous.leftSizes),
        ...parseNumberList(previous.rightSizes),
        0,
      )
    : 0;

  const update = (key: DraftKey, value: string) => {
    setDraft((previousDraft) => {
      const nextDraft = { ...previousDraft, [key]: value };
      if (
        key === "leftSizes" &&
        !value.trim() &&
        previousDraft.leftSizes.trim() &&
        previousDraft.leftCount ===
          String(parseNumberList(previousDraft.leftSizes).length)
      ) {
        nextDraft.leftCount = "";
      }
      if (
        key === "rightSizes" &&
        !value.trim() &&
        previousDraft.rightSizes.trim() &&
        previousDraft.rightCount ===
          String(parseNumberList(previousDraft.rightSizes).length)
      ) {
        nextDraft.rightCount = "";
      }
      return nextDraft;
    });
    setErrors((previousErrors) => ({
      ...previousErrors,
      [key]: undefined,
      form: undefined,
    }));
  };

  const validate = (): ValidationErrors => {
    const nextErrors: ValidationErrors = {};
    if (!dateISO) {
      nextErrors.date = t(
        "care.overview.calendar.monitoring_log.modal.date_required",
      );
    } else if (dateISO > todayISO) {
      nextErrors.date = t(
        "care.overview.calendar.monitoring_log.modal.date_future",
      );
    }
    const checks: [DraftKey, string | null][] = [
      ["leftSizes", validateSizeList(draft.leftSizes, "Left follicle sizes")],
      [
        "rightSizes",
        validateSizeList(draft.rightSizes, "Right follicle sizes"),
      ],
      ["leftCount", validateNumber(draft.leftCount, 60, "Left count")],
      ["rightCount", validateNumber(draft.rightCount, 60, "Right count")],
      [
        "liningThickness",
        validateNumber(
          draft.liningThickness,
          50,
          "Endometrial lining thickness",
        ),
      ],
      ["estradiol", validateNumber(draft.estradiol, 10000, "Estradiol")],
      ["lh", validateNumber(draft.lh, 100, "LH")],
      ["fsh", validateNumber(draft.fsh, 100, "FSH")],
      ["progesterone", validateNumber(draft.progesterone, 50, "Progesterone")],
    ];
    checks.forEach(([key, error]) => {
      if (error) nextErrors[key] = error;
    });

    const hasValue = MONITORING_VALUE_KEYS.some((key) => draft[key].trim());
    const hasExistingValue = MONITORING_VALUE_KEYS.some((key) =>
      current[key].trim(),
    );
    if (!hasValue && !hasExistingValue) {
      nextErrors.form = t(
        "care.overview.calendar.monitoring_log.modal.needs_one_value",
      );
    }
    return nextErrors;
  };

  const handleSave = async () => {
    if (saving) return;
    const nextErrors = validate();
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      requestAnimationFrame(() => {
        if (nextErrors.date) {
          formScrollRef.current?.scrollTo({ y: 0, animated: true });
          return;
        }
        formScrollRef.current?.scrollToEnd({ animated: true });
      });
      return;
    }
    setSaving(true);
    setErrors({});
    setSaveFailed(false);
    try {
      await onSave(
        {
          ...draft,
          leftCount,
          rightCount,
        },
        sourceDocumentIds,
      );
      onClose();
    } catch {
      // Said here, above the buttons, so the dialog and her entries stay.
      setSaveFailed(true);
    } finally {
      setSaving(false);
    }
  };

  const handleImportedMonitoring = (data: Record<string, unknown>) => {
    const imported = monitoringValuesFromOCR(data);
    setDraft((previousDraft) => {
      const nextDraft = { ...previousDraft };
      (Object.keys(imported) as DraftKey[]).forEach((key) => {
        const value = imported[key];
        if (typeof value === "string" && value && !nextDraft[key].trim()) {
          nextDraft[key] = value;
        }
      });
      return nextDraft;
    });
    setErrors((previousErrors) => ({ ...previousErrors, form: undefined }));
    setSourceNotice(true);
  };

  const handleUpload = () => {
    if (!dateISO) {
      setErrors((previousErrors) => ({
        ...previousErrors,
        date: t("care.overview.calendar.monitoring_log.modal.date_required"),
      }));
      return;
    }
    if (dateISO > todayISO) {
      setErrors((previousErrors) => ({
        ...previousErrors,
        date: t("care.overview.calendar.monitoring_log.modal.date_future"),
      }));
      return;
    }
    if (onUploadReport) {
      onUploadReport(dateISO, handleImportedMonitoring, (result) => {
        const documentId = result.documentId;
        if (!documentId) return;
        setSourceDocumentIds((currentIds) =>
          currentIds.includes(documentId)
            ? currentIds
            : [...currentIds, documentId],
        );
      });
      return;
    }
    setSourceNotice(true);
  };

  const handleDateChange = (nextDateISO: string) => {
    setSourceNotice(false);
    setErrors((previousErrors) => ({
      ...previousErrors,
      date: undefined,
      form: undefined,
    }));
    onDateChange?.(nextDateISO);
  };

  const dialogMaxHeight = Math.min(820, Math.max(520, height * 0.9));

  return (
    <Dialog
      containerStyle={{ maxHeight: dialogMaxHeight }}
      visible={open}
      onClose={onClose}
      testID="monitoring-entry-dialog"
      closeButton="minimal"
      surfaceStyle={{ maxHeight: dialogMaxHeight }}
      contentStyle={{
        paddingTop: 22,
        paddingHorizontal: 22,
        paddingBottom: 18,
        maxHeight: dialogMaxHeight,
      }}
    >
      <View style={{ flex: 1, minHeight: 0 }}>
        <DialogIcon
          source={MONITORING_DIALOG_ILLUSTRATION}
          width={78}
          height={72}
          style={{ marginBottom: 16 }}
        />
        <DialogHeader
          align="center"
          title={t("care.overview.calendar.monitoring_log.modal.title")}
          subtitle={t("care.overview.calendar.monitoring_log.modal.subtitle")}
          style={{
            width: "100%",
            marginBottom: 16,
          }}
          subtitleStyle={{ textAlign: "center", width: "100%" }}
        />

        <ScrollView
          ref={formScrollRef}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          style={{ flexShrink: 1 }}
          contentContainerStyle={{ gap: 10, paddingBottom: 4 }}
        >
          <DateField
            label={t(
              "care.overview.calendar.monitoring_log.modal.date_input_label",
            )}
            value={dateISO}
            onChange={handleDateChange}
            error={errors.date}
            disabled={saving}
            testID="monitoring-date-field"
          />

          {previous ? (
            <CardSurface variant="panel" style={{ padding: 12, gap: 9 }}>
              <View style={{ flexDirection: "row", gap: 8 }}>
                <View style={{ flex: 1, gap: 2 }}>
                  <Text variant="label" style={{ fontSize: 13 }}>
                    {t(
                      "care.overview.calendar.monitoring_log.modal.previous_title",
                    )}
                  </Text>
                  <Text
                    variant="caption"
                    style={{ color: colors.textLight, fontSize: 11 }}
                  >
                    {formatDateLabel(previous.date)}
                  </Text>
                </View>
                <Info size={16} color={colors.textFaint} />
              </View>
              <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 7 }}>
                <SummaryDetail
                  icon={Egg}
                  label={t(
                    "care.overview.calendar.monitoring_log.field.follicles_label",
                  )}
                  value={`${previousLeftCount || "—"} · ${previousRightCount || "—"}`}
                />
                <SummaryDetail
                  icon={Ruler}
                  label={t(
                    "care.overview.calendar.monitoring_log.field.lead_size_label",
                  )}
                  value={previousLeadSize ? `${previousLeadSize} mm` : "—"}
                />
                <SummaryDetail
                  icon={FlaskConical}
                  label="E2"
                  value={
                    previous.estradiol
                      ? `${previous.estradiol} ${previous.estradiolUnit}`
                      : "—"
                  }
                />
              </View>
              <Text
                variant="caption"
                style={{
                  color: colors.textFaint,
                  fontSize: 11,
                  lineHeight: 15,
                }}
              >
                {t(
                  "care.overview.calendar.monitoring_log.modal.previous_detail",
                )}
              </Text>
            </CardSurface>
          ) : null}

          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 10,
              paddingVertical: 4,
            }}
          >
            <View style={{ flex: 1, gap: 2 }}>
              <Text variant="label" style={{ fontSize: 13 }}>
                {t("care.overview.calendar.monitoring_log.modal.import_title")}
              </Text>
              <Text
                variant="caption"
                style={{
                  color: colors.textLight,
                  fontSize: 11,
                  lineHeight: 15,
                }}
              >
                {t("care.overview.calendar.monitoring_log.modal.import_detail")}
              </Text>
            </View>
            <CompactActionButton
              label={t(
                "care.overview.calendar.monitoring_log.modal.import_action",
              )}
              action="import_monitoring_document"
              variant="secondary"
              size="sm"
              leadingIcon={UploadCloud}
              onPress={handleUpload}
            />
          </View>
          {sourceNotice ? (
            <Text
              variant="caption"
              style={{ color: colors.textLight, fontSize: 11, lineHeight: 15 }}
            >
              {t("care.overview.calendar.monitoring_log.modal.import_notice")}
            </Text>
          ) : null}

          <MonitoringEntryTabs active={section} onChange={setSection} />

          {section === "ultrasound" ? (
            <View style={{ gap: 14 }}>
              <View style={{ flexDirection: "row", gap: 12 }}>
                <View style={{ flex: 1 }}>
                  <FormInput
                    label={t(
                      "care.overview.calendar.monitoring_log.modal.left_follicles",
                    )}
                    hint={t(
                      "care.overview.calendar.monitoring_log.modal.comma_separated",
                    )}
                    suffix="mm"
                    error={errors.leftSizes}
                    value={draft.leftSizes}
                    onChangeText={(value) => update("leftSizes", value)}
                    placeholder={t(
                      "care.overview.calendar.monitoring_log.modal.sizes_placeholder",
                    )}
                    accessibilityLabel={t(
                      "care.overview.calendar.monitoring_log.modal.left_follicles",
                    )}
                    keyboardType="decimal-pad"
                  />
                </View>
                <View style={{ flex: 1 }}>
                  <FormInput
                    label={t(
                      "care.overview.calendar.monitoring_log.modal.right_follicles",
                    )}
                    hint={t(
                      "care.overview.calendar.monitoring_log.modal.comma_separated",
                    )}
                    suffix="mm"
                    error={errors.rightSizes}
                    value={draft.rightSizes}
                    onChangeText={(value) => update("rightSizes", value)}
                    placeholder={t(
                      "care.overview.calendar.monitoring_log.modal.sizes_placeholder",
                    )}
                    accessibilityLabel={t(
                      "care.overview.calendar.monitoring_log.modal.right_follicles",
                    )}
                    keyboardType="decimal-pad"
                  />
                </View>
              </View>
              <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 7 }}>
                <SummaryDetail
                  icon={Egg}
                  label={t(
                    "care.overview.calendar.monitoring_log.modal.derived_count_left",
                  )}
                  value={leftCount}
                />
                <SummaryDetail
                  icon={Egg}
                  label={t(
                    "care.overview.calendar.monitoring_log.modal.derived_count_right",
                  )}
                  value={rightCount}
                />
                <SummaryDetail
                  icon={Ruler}
                  label={t(
                    "care.overview.calendar.monitoring_log.modal.derived_lead",
                  )}
                  value={leadSize ? `${leadSize} mm` : ""}
                />
              </View>
              <View style={{ flexDirection: "row", gap: 12 }}>
                <View style={{ flex: 1 }}>
                  <FormInput
                    label={t(
                      "care.overview.calendar.monitoring_log.modal.fallback_count_left",
                    )}
                    error={errors.leftCount}
                    value={draft.leftCount}
                    onChangeText={(value) => update("leftCount", value)}
                    placeholder={t(
                      "care.overview.calendar.monitoring_log.modal.count_placeholder",
                    )}
                    accessibilityLabel={t(
                      "care.overview.calendar.monitoring_log.modal.fallback_count_left",
                    )}
                    editable={leftSizes.length === 0}
                    keyboardType="number-pad"
                  />
                </View>
                <View style={{ flex: 1 }}>
                  <FormInput
                    label={t(
                      "care.overview.calendar.monitoring_log.modal.fallback_count_right",
                    )}
                    error={errors.rightCount}
                    value={draft.rightCount}
                    onChangeText={(value) => update("rightCount", value)}
                    placeholder={t(
                      "care.overview.calendar.monitoring_log.modal.count_placeholder",
                    )}
                    accessibilityLabel={t(
                      "care.overview.calendar.monitoring_log.modal.fallback_count_right",
                    )}
                    editable={rightSizes.length === 0}
                    keyboardType="number-pad"
                  />
                </View>
              </View>
              <FormInput
                label={t(
                  "care.overview.calendar.monitoring_log.modal.lining_label",
                )}
                suffix="mm"
                error={errors.liningThickness}
                value={draft.liningThickness}
                onChangeText={(value) => update("liningThickness", value)}
                placeholder={t(
                  "care.overview.calendar.monitoring_log.modal.lining_placeholder",
                )}
                accessibilityLabel={t(
                  "care.overview.calendar.monitoring_log.modal.lining_label",
                )}
                keyboardType="decimal-pad"
              />
            </View>
          ) : (
            <View style={{ gap: 14 }}>
              <LabValueField
                label={t(
                  "care.overview.calendar.monitoring_log.modal.estradiol_label",
                )}
                value={draft.estradiol}
                onChangeValue={(value) => update("estradiol", value)}
                unit={draft.estradiolUnit}
                onChangeUnit={(unit) => update("estradiolUnit", unit)}
                marker="Estradiol"
                numeric
                error={errors.estradiol}
                placeholder="e.g. 850"
                unitAction="pick_bloodwork_unit"
              />
              <LabValueField
                label={t(
                  "care.overview.calendar.monitoring_log.modal.lh_label",
                )}
                value={draft.lh}
                onChangeValue={(value) => update("lh", value)}
                unit={draft.lhUnit}
                onChangeUnit={(unit) => update("lhUnit", unit)}
                marker="LH"
                numeric
                error={errors.lh}
                placeholder="e.g. 4.2"
                unitAction="pick_bloodwork_unit"
              />
              <LabValueField
                label={t(
                  "care.overview.calendar.monitoring_log.modal.fsh_label",
                )}
                value={draft.fsh}
                onChangeValue={(value) => update("fsh", value)}
                unit={draft.fshUnit}
                onChangeUnit={(unit) => update("fshUnit", unit)}
                marker="FSH"
                numeric
                error={errors.fsh}
                placeholder="Optional"
                unitAction="pick_bloodwork_unit"
              />
              <LabValueField
                label={t(
                  "care.overview.calendar.monitoring_log.modal.progesterone_label",
                )}
                value={draft.progesterone}
                onChangeValue={(value) => update("progesterone", value)}
                unit={draft.progesteroneUnit}
                onChangeUnit={(unit) => update("progesteroneUnit", unit)}
                marker="Progesterone"
                numeric
                error={errors.progesterone}
                placeholder="Optional"
                unitAction="pick_bloodwork_unit"
              />
            </View>
          )}

          <FormInput
            label={t("care.overview.calendar.monitoring_log.modal.notes_label")}
            value={draft.notes}
            onChangeText={(value) => update("notes", value)}
            placeholder={t(
              "care.overview.calendar.monitoring_log.modal.notes_placeholder",
            )}
            accessibilityLabel={t(
              "care.overview.calendar.monitoring_log.modal.notes_label",
            )}
            multiline
          />

          {errors.form ? (
            <ActionBanner variant="important" message={errors.form} />
          ) : null}
        </ScrollView>

        {saveFailed ? (
          <ActionBanner
            variant="important"
            message={t(
              "care.overview.calendar.monitoring_log.modal.save_failed_keep_draft",
            )}
            actionLabel={t("clinical_data.retry")}
            action="retry_save_monitoring_entry"
            actionIcon={RotateCcw}
            onAction={() => void handleSave()}
            actionLoading={saving}
            testID="monitoring-entry-save-failed"
            style={{ marginTop: 12 }}
          />
        ) : null}

        <DialogActions
          style={{
            borderTopWidth: 1,
            borderTopColor: semanticColors.border.subtle,
            paddingTop: 14,
            marginTop: 14,
            alignItems: "stretch",
          }}
        >
          <SecondaryButton
            label={t("care.overview.calendar.monitoring_log.modal.cancel")}
            onPress={onClose}
            disabled={saving}
            flex={1}
          />
          <PrimaryButton
            label={t("care.overview.calendar.monitoring_log.modal.save")}
            onPress={() => void handleSave()}
            loading={saving}
            disabled={saving}
            flex={1.35}
          />
          <IconButton
            accessibilityLabel={t(
              "care.overview.calendar.monitoring_log.modal.import_action",
            )}
            onPress={handleUpload}
            disabled={saving}
            size={48}
            surface="soft"
            style={{ width: 48 }}
          >
            <UploadCloud size={18} color={colors.textLight} strokeWidth={1.8} />
          </IconButton>
        </DialogActions>
      </View>
    </Dialog>
  );
}
